import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import {
  endorsementCatalog,
  endorsementWizard,
  type EndorsementDetail,
  type EndorsementDocument,
  type EndorsementDraftInput,
} from '@/shared/api/resources';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { enviarArquivoDoEndosso } from './endosso';

/**
 * Passo "Motivo e documentos", comum aos tres assistentes (END-008, END-027, END-028,
 * END-072, END-073, END-137).
 *
 * O motivo vem da lista do subtipo; escolher o motivo cria no endosso os documentos que
 * ele exige. O arquivo sobe pelo proprio endosso, em tres passos (reservar, enviar,
 * confirmar): a corretora nao le o repositorio geral de documentos.
 */

const situacoes: Record<
  EndorsementDocument['status'],
  { label: string; tone: 'warning' | 'success' | 'neutral' }
> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  recebido: { label: 'Enviado', tone: 'success' },
  dispensado: { label: 'Dispensado', tone: 'neutral' },
};

export function ReasonAndDocumentsStep({
  endorsement,
  editavel,
  podeEnviar,
  salvando,
  onSalvar,
  onProximo,
}: {
  endorsement: EndorsementDetail;
  editavel: boolean;
  podeEnviar: boolean;
  salvando: boolean;
  onSalvar: (input: EndorsementDraftInput) => void;
  onProximo: (input: EndorsementDraftInput | null) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState(endorsement.endorsement_reason_id ?? '');
  const [observacao, setObservacao] = useState(endorsement.reason);
  const [nomeDoDocumento, setNomeDoDocumento] = useState('');
  const subtipo = endorsement.endorsement_subtype_id ?? '';

  const motivos = useQuery({
    queryKey: ['endorsement-reasons', subtipo],
    queryFn: ({ signal }) => endorsementCatalog.reasons(subtipo, false, signal),
    enabled: subtipo !== '',
    retry: false,
  });

  const recarregar = () =>
    void queryClient.invalidateQueries({ queryKey: ['endorsement', endorsement.id] });

  const enviar = useMutation({
    mutationFn: ({ documentId, arquivo }: { documentId: string; arquivo: File }) =>
      enviarArquivoDoEndosso(endorsement.id, documentId, arquivo),
    onSuccess: () => {
      toast.success('Upload do arquivo realizado com sucesso!');
      recarregar();
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Algo deu errado, tente novamente'),
  });

  const nomear = useMutation({
    mutationFn: () =>
      endorsementWizard.requireDocument(endorsement.id, nomeDoDocumento),
    onSuccess: () => {
      setNomeDoDocumento('');
      recarregar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const baixar = useMutation({
    mutationFn: (documentId: string) =>
      endorsementWizard.download(endorsement.id, documentId),
    onSuccess: (link) => window.open(link.url, '_blank', 'noopener'),
    onError: (error: Error) => toast.error(error.message),
  });

  const mudouMotivo = motivo !== (endorsement.endorsement_reason_id ?? '');
  const mudouObservacao = observacao !== endorsement.reason;
  const pedido: EndorsementDraftInput = {
    ...(mudouMotivo ? { endorsement_reason_id: motivo === '' ? null : motivo } : {}),
    ...(mudouObservacao ? { reason: observacao } : {}),
  };
  const documentos = endorsement.documents ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Motivo do endosso"
          description="A lista do subtipo escolhido."
        />
        <CardBody className="flex flex-col gap-4">
          {motivos.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar os motivos">
              {motivos.error.message}
            </Alert>
          ) : null}
          {motivos.isSuccess && motivos.data.length === 0 ? (
            <Alert tone="warning" title="Nenhum motivo ativo">
              O subtipo nao tem motivo ativo configurado. A configuracao fica em
              Endossos, Configuracao.
            </Alert>
          ) : null}
          <Select
            label="Motivo do endosso"
            required
            disabled={!editavel}
            placeholder={motivos.isPending ? 'Carregando motivos' : 'Selecione'}
            options={(motivos.data ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
          />
          <Textarea
            label="Observacao"
            disabled={!editavel}
            value={observacao}
            maxLength={2000}
            placeholder="Descreva o motivo do endosso"
            onChange={(event) => setObservacao(event.target.value)}
          />
          {editavel && mudouMotivo ? (
            <div>
              <Button
                variant="secondary"
                loading={salvando}
                onClick={() => onSalvar(pedido)}
              >
                Aplicar o motivo e listar os documentos
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Documentos"
          description="Caso seja necessario, faca o upload de documentos para analise da seguradora!"
        />
        <CardBody className="flex flex-col gap-3">
          {documentos.length === 0 ? (
            <p className="text-sm text-ink-600">
              Nenhum documento no endosso. Os documentos exigidos aparecem ao escolher o
              motivo.
            </p>
          ) : null}
          <ul className="flex flex-col gap-2">
            {documentos.map((documento) => (
              <LinhaDoDocumento
                key={documento.id}
                documento={documento}
                podeEnviar={podeEnviar}
                enviando={
                  enviar.isPending && enviar.variables?.documentId === documento.id
                }
                onArquivo={(arquivo) =>
                  enviar.mutate({ documentId: documento.id, arquivo })
                }
                onBaixar={() => baixar.mutate(documento.id)}
              />
            ))}
          </ul>
          {podeEnviar ? (
            <form
              className="flex flex-wrap items-end gap-2 border-t border-line pt-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (nomeDoDocumento.trim() !== '') nomear.mutate();
              }}
            >
              <div className="min-w-56 flex-1">
                <Field
                  label="Nomear documento:"
                  value={nomeDoDocumento}
                  maxLength={200}
                  onChange={(event) => setNomeDoDocumento(event.target.value)}
                />
              </div>
              <Button type="submit" variant="secondary" loading={nomear.isPending}>
                Upload de Documento
              </Button>
            </form>
          ) : null}
        </CardBody>
      </Card>

      {editavel ? (
        <div className="flex justify-end">
          <Button
            loading={salvando}
            disabled={motivo === ''}
            onClick={() => onProximo(mudouMotivo || mudouObservacao ? pedido : null)}
          >
            Proximo
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function LinhaDoDocumento({
  documento,
  podeEnviar,
  enviando,
  onArquivo,
  onBaixar,
}: {
  documento: EndorsementDocument;
  podeEnviar: boolean;
  enviando: boolean;
  onArquivo: (arquivo: File) => void;
  onBaixar: () => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const situacao = situacoes[documento.status];
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
      <div className="min-w-0 text-sm">
        <p className="font-medium text-ink-900">
          {documento.title}
          {documento.required ? <span className="text-danger-700"> *</span> : null}
        </p>
        <p className="truncate text-2xs text-ink-500">
          {documento.filename ||
            (documento.required ? 'documentos obrigatorios' : 'opcional')}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <Badge tone={situacao.tone}>{situacao.label}</Badge>
        {documento.current_version > 0 ? (
          <AcaoComIcone acao="baixar" rotulo="Baixar" onClick={onBaixar} />
        ) : null}
        {podeEnviar ? (
          <>
            <input
              ref={entrada}
              type="file"
              className="sr-only"
              aria-label={`Arquivo de ${documento.title}`}
              onChange={(event) => {
                const arquivo = event.target.files?.[0];
                if (arquivo) onArquivo(arquivo);
                event.target.value = '';
              }}
            />
            <AcaoComIcone
              acao={documento.current_version > 0 ? 'enviarArquivo' : 'anexar'}
              rotulo={documento.current_version > 0 ? 'Substituir' : 'Anexar'}
              loading={enviando}
              onClick={() => entrada.current?.click()}
            />
          </>
        ) : null}
      </div>
    </li>
  );
}
